const { scheduleBackgroundStartup, measureStartupTask } = require('services/auth-management/background-startup');
const T = require('prop-types');
const { push: Push, replace: Replace } = require('connected-react-router');
const { makeActionCreator } = require('../utils/redux-helpers');
const { AUTH: Types } = require('../action-types');
const NoRedirectRoutes = require('../routes/no-redirect-routes');
const { initializeAppForSignup } = require('utils/initializeAppForSignup');
const { recoverFromBootstrapError } = require('utils/auth-session-recovery');
const CreateCommunicationService = require('services/communication');
const AuthConstants = require('services/auth-management/constants');
const AuthMfa = require('services/auth-management/mfa');
const RoutingTable = require('services/auth-management/routing-table');
const AuthPersistence = require('services/auth-management/persistence');
const AuthStateTransitions = require('services/auth-management/state-transitions');
const Continuation = require('services/auth-management/continuation');
const AuthDebug = require('services/auth-management/debug');
const BootstrapCache = require('services/auth-management/bootstrap-cache');

const internals = {};
internals.initializations = new WeakMap();

module.exports = (context) => {

    const api       = context.api.nearpeer;
    const communication = CreateCommunicationService.fromContext(context);
    const actions   = context.actions;
    const redux     = context.redux.hooks;
    const selectors = context.selectors.all;
    const handlers  = context.handlers;

    const cache = {};

    return {
        login: makeActionCreator(Types.LOGIN, { email: T.string, password: T.string, token: T.string }, {
            async: (payload) => AuthDebug.captureApiCall({
                name: 'auth.login',
                request: payload,
                func: () => api.authentication.login(payload)
            }),
            after: async ({ dispatch, payload }) => {

                if (RoutingTable.isPendingMfaStatus(payload.result?.status)) {
                    RoutingTable.routePendingMfa({
                        currentContext: selectors.getLoginContext(redux.getState()),
                        dispatch,
                        request: payload.request,
                        response: payload.result,
                        updateContext: actions.auth.updateLoginContext
                    });
                    return undefined;
                }

                const restoredState = redux.getState();
                const resumedMfaContext = internals.getResumableMfaContext({
                    selectors,
                    state: restoredState
                });

                if (payload.request?.token && resumedMfaContext.mfaToken) {
                    AuthDebug.recordFlow('mfa_challenge_resumed_after_token_restore', {
                        mfaMethod: resumedMfaContext.mfaMethod
                    });
                    return undefined;
                }

                const { isVerified, schoolId } = payload.result;

                const {
                    initializeApp,
                    initializeIncompleteSignupFromAuth
                } = internals;

                // For users reauthing with a stored token
                if (isVerified) {
                    if (payload.request?.token) {
                        const restored = await BootstrapCache.restore({ context, token: payload.request.token, schoolId });
                        const requestedSchoolId = RoutingTable.getRequestedSchoolId();

                        if (restored && (!requestedSchoolId || requestedSchoolId === Number(schoolId))) {
                            redux.dispatch(actions.app.setMainAppLoading({ appLoading: false }));
                        }
                    }

                    return await initializeApp(context, schoolId, {
                        ...internals.consumeLoginContinuation(context)
                    });
                }

                // isVerified === false
                // User hasn't finished signing up
                return await initializeIncompleteSignupFromAuth(context, {
                    signupContext: payload.result?.signupContext
                });
            }
        }),
        updateLoginContext: makeActionCreator(Types.UPDATE_LOGIN_CONTEXT, {
            email: T.string,
            schoolId: T.number,
            roleId: T.number,
            sso: T.string,
            verificationToken: T.string,
            mfaToken: T.string,
            mfaMethod: T.string,
            mfaPurpose: T.string,
            mfaNeedsPhone: T.bool,
            mfaPhone: T.string,
            mfaPhoneCountry: T.string,
            mfaCooldownCode: T.string,
            mfaCooldownUntil: T.number,
            maskedDestination: T.string,
            canChangePhone: T.bool,
            authMethod: T.string,
            authStatus: T.string,
            authFlowSource: T.string,
            hasPassword: T.bool,
            lastLoginEmail: T.string,
            forceSSOLoginPromptEmail: T.string,
            skipSSOLoginPromptEmail: T.string,
            redirect: T.string,
            redirectTarget: T.string,
            policy: T.object
        }),
        bootstrapMainApp: ({ schoolId, redirect, target } = {}) => async () => internals.bootstrapMainApp(context, {
            redirect,
            schoolId,
            target
        }),
        loginWithVerificationToken: makeActionCreator(Types.LOGIN_TOKEN, { verificationToken: T.string }, {
            async: async (payload) => {

                const result = await api.authentication.loginWithVerificationToken(payload);
                const authContext = internals.getCurrentAuthContext({
                    selectors,
                    state: redux.getState()
                });

                return RoutingTable.assertMfaAllowedForAuthContext({
                    authContext,
                    result
                });
            },
            after: async ({ dispatch, payload }) => {

                if (RoutingTable.isPendingMfaStatus(payload.result?.status)) {
                    RoutingTable.routePendingMfa({
                        currentContext: selectors.getLoginContext(redux.getState()),
                        dispatch,
                        request: payload.request,
                        response: payload.result,
                        updateContext: actions.auth.updateLoginContext
                    });
                    return undefined;
                }

                const { schoolId } = payload.result;

                const { initializeApp } = internals;

                // if (!NoRedirectRoutes.includes(window.location.pathname)) {
                //     redux.dispatch(Push(AuthConstants.authFlow.routes.app.welcome));
                // }

                await initializeApp(context, schoolId, {
                    ...internals.consumeLoginContinuation(context)
                });
            }
        }),
        loginSSO: makeActionCreator(Types.LOGIN_SSO_TOKEN, { token: T.string, sso: T.string, email: T.string }, {
            async: (payload) => AuthDebug.captureApiCall({
                name: 'auth.loginSSO',
                request: payload,
                func: async () => {

                    const result = await api.authentication.loginSSO(payload);

                    return RoutingTable.assertMfaAllowedForAuthContext({
                        authContext: { authMethod: AuthConstants.authFlow.methods.sso },
                        result
                    });
                }
            }),
            after: async ({ payload }) => {

                const { initializeApp } = internals;

                const { isVerified, schoolId } = payload.result;

                if (isVerified && !payload.result?.tokens?.nearpeer) {
                    AuthDebug.error('verified_sso_login_missing_nearpeer_token', {
                        sso: payload.request?.sso,
                        schoolId,
                        result: payload.result
                    });

                    throw new Error('Verified SSO login is missing a Nearpeer token');
                }

                if (isVerified) {
                    return await initializeApp(context, schoolId, {
                        ...internals.consumeLoginContinuation(context)
                    });
                }

                return await internals.initializeIncompleteSignupFromAuth(context, {
                    signupContext: payload.result?.signupContext
                });
            }
        }),
        startMfaSetup: makeActionCreator(Types.START_MFA_SETUP, {
            mfaToken: T.string,
            phone: T.string,
            phoneCountry: T.string
        }, {
            async: api.authentication.startMfaSetup,
            after: ({ dispatch, payload }) => internals.updatePendingMfaOwner(context, {
                dispatch,
                payload
            })
        }),
        resendMfaChallenge: makeActionCreator(Types.RESEND_MFA_CHALLENGE, { mfaToken: T.string }, {
            async: api.authentication.resendMfaChallenge,
            after: ({ dispatch, payload }) => internals.updatePendingMfaOwner(context, {
                dispatch,
                payload
            })
        }),
        resetMfaPhone: makeActionCreator(Types.RESET_MFA_PHONE, { mfaToken: T.string }, {
            async: api.authentication.resetMfaPhone,
            after: ({ dispatch, payload }) => internals.updatePendingMfaOwner(context, {
                dispatch,
                payload
            })
        }),
        verifyMfa: makeActionCreator(Types.VERIFY_MFA, { mfaToken: T.string, code: T.string }, {
            async: api.authentication.verifyMfa,
            after: async ({ dispatch, payload }) => {

                const loginContext = selectors.getLoginContext?.(redux.getState()) || {};
                const signupContext = selectors.getSignupContext(redux.getState()) || {};
                const mfaCompletion = AuthMfa.classifyCompletion({
                    signupContext,
                    mfaToken: payload.request?.mfaToken
                });
                const isSignupMfa = mfaCompletion === AuthMfa.COMPLETION_CLASSIFICATIONS.signupCompletion;
                const isProfilePhoneUpdate = mfaCompletion === AuthMfa.COMPLETION_CLASSIFICATIONS.authenticated
                    && loginContext.authFlowSource === AuthConstants.authFlow.sources.profilePhoneUpdate;
                const verificationSucceeded = !RoutingTable.isPendingMfaStatus(payload.result?.status);

                if (isSignupMfa && verificationSucceeded) {
                    const pendingPreferences = signupContext.pendingPreferences || {};

                    dispatch(actions.signup.updateContext({
                        mfaToken: null,
                        mfaMethod: null,
                        mfaPurpose: null,
                        mfaNeedsPhone: null,
                        mfaPhone: null,
                        mfaPhoneCountry: null,
                        mfaCooldownCode: null,
                        mfaCooldownUntil: null,
                        maskedDestination: null,
                        canChangePhone: null,
                        pendingPreferences: null
                    }));

                    if (!selectors.getIsAuthenticated(redux.getState())) {
                        throw new Error('Cannot complete signup MFA without the signup auth token');
                    }

                    await dispatch(actions.profileManagement.updateUserPreferences(pendingPreferences));
                    dispatch(actions.signup.signupCompleted({ SignupCompleted: true }));
                    const continuation = AuthStateTransitions.completeSuccessfulAuthentication(signupContext.email);

                    return internals.initializeApp(
                        context,
                        payload.result?.schoolId || signupContext.schoolId,
                        {
                            redirect: continuation.redirect || RoutingTable.REDIRECTS.welcome,
                            target: continuation.target
                        }
                    );
                }

                if (payload.result?.status === 'verified') {
                    if (isProfilePhoneUpdate) {
                        const continuation = internals.consumeLoginContinuation(context);
                        Continuation.consume();
                        redux.dispatch(Replace(RoutingTable.resolve(RoutingTable.ROUTING_KINDS.postAuth, continuation)));
                        dispatch(actions.alerts.notification.push({
                            message: 'Profile and phone number updated'
                        }));
                    }

                    return undefined;
                }

                const { initializeApp } = internals;
                const { isVerified, schoolId } = payload.result;

                if (isVerified) {
                    const continuation = internals.consumeLoginContinuation(context);

                    return await initializeApp(context, schoolId, {
                        redirect: continuation.redirect || RoutingTable.REDIRECTS.welcome,
                        target: continuation.target
                    });
                }

                return await internals.initializeIncompleteSignupFromAuth(context, {
                    signupContext: payload.result?.signupContext
                });
            }
        }),
        logout: makeActionCreator(Types.LOGOUT, { redirect: T.oneOf([T.string, T.bool]) }, {
            async: async () => {

                try {
                    const token = await selectors.getFirebaseToken(redux.getState());

                    if (typeof token !== 'undefined') {
                        await api.authentication.deleteFirebaseToken({ token });
                    }
                }
                catch (err) {

                    if (err) {
                        console.log(err);
                    }

                    // TODO resolve clearing twilio endpoint? Probably if returns a 404 -- backend should probably catch this and handle it by decrementing the twilio endpoint count
                }

                const currentSchool = selectors.getCurrentSchool(redux.getState());

                cache.school = currentSchool;

                await api.authentication.logout();

                communication.shutdown();
                handlers.communication.stopGlobalListening();
            },
            after: ({ dispatch, payload }) => {

                internals.clearTransientAuthState({ preserveContinuation: payload.request?.redirect === false });

                if (payload.request?.redirect !== false) {
                    dispatch(Push(payload.request?.redirect || AuthConstants.authFlow.routes.home));
                }

                dispatch(actions.app.setShouldRefreshSearch({
                    discover: true,
                    group: true
                }));

                delete cache.school;
            }
        }),
        closeAccount: makeActionCreator(Types.CLOSE_ACCOUNT, { closeAccountReason : T.string }, {
            async: ({ closeAccountReason }) => {

                //TODO create close account
                //change to remove firebase token
                return Promise.resolve(selectors.getFirebaseToken(redux.getState())).then((token) => {

                    if (typeof token !== 'undefined') {
                        return api.authentication.deleteFirebaseToken({ token });
                    }

                }).then(() => {

                    return api.authentication.closeAccount(closeAccountReason);

                }).then(() =>

                    undefined

                ).catch((err) => {

                    if (err) {
                        console.log(err);
                    }

                    // TODO resolve clearing twilio endpoint?  Probably if returns a 404 -- backend should probably catch this and handle it by decrementing the twilio endpoint count
                    return api.authentication.closeAccount(closeAccountReason);
                });
            },
            after: ({ dispatch }) => {

                internals.clearTransientAuthState();

                dispatch(Push(AuthConstants.authFlow.routes.home));

                return communication.initialize({ reset: true });
            }
        }),
        accountIsClosedClearAll: makeActionCreator(Types.ACCOUNT_CLOSED_CLEAR, {}, {
            after: ({ dispatch }) => {

                internals.clearTransientAuthState();

                dispatch(Push(AuthConstants.authFlow.routes.home));

                return communication.initialize({ reset: true });
            }
        }),
        logoutSync: makeActionCreator(Types.LOGOUT_SYNC, { redirect: T.oneOf([T.string, T.bool]) }, {
            after: ({ dispatch, payload }) => {

                communication.shutdown();
                handlers.communication.stopGlobalListening();
                const redirect = payload?.request?.redirect ?? payload?.redirect;
                internals.clearTransientAuthState({ preserveContinuation: redirect === false });

                if (redirect !== false) {
                    dispatch(Push(redirect || AuthConstants.authFlow.routes.home));
                }

                dispatch(actions.app.setShouldRefreshSearch({
                    discover: true,
                    group: true
                }));

                return communication.initialize({ reset: true });
            }
        }),
        requestPasswordReset: makeActionCreator(Types.REQUEST_PASSWORD_RESET, { email: T.string }, {
            async: api.authentication.requestPasswordReset
        }),
        resetPassword: makeActionCreator(Types.RESET_PASSWORD, { password: T.string, resetToken: T.string }, {
            async: api.authentication.resetPassword,
            after: ({ dispatch, payload }) => {

                dispatch(Push('/'));
                dispatch(actions.alerts.notification.push({
                    message: 'Password was reset! Please login.'
                }));

                return payload;
            }
        }),
        checkResetToken: makeActionCreator(Types.CHECK_RESET_TOKEN, { resetToken: T.string }, {
            async: api.authentication.checkResetToken
        }),
        loginInputFailure: makeActionCreator(Types.LOGIN_INPUT_FAILURE, {}),
        updateTwilioToken: makeActionCreator(Types.UPDATE_TWILIO_TOKEN, {}, {
            async: api.authentication.updateTwilioToken,
            after: ({ payload }) => {

                const token = payload?.result?.token;

                if (typeof token !== 'string' || !token.trim()) {
                    return undefined;
                }

                return communication.updateToken({ token });
            }
        })
    };
};

internals.updatePendingMfaOwner = (context, { dispatch, payload }) => {

    const selectors = context.selectors.all;
    const state = context.redux.hooks.getState();
    const loginContext = selectors.getLoginContext?.(state) || {};
    const signupContext = selectors.getSignupContext(state) || {};
    const signupOwnsChallenge = signupContext.mfaToken === payload.request?.mfaToken;
    const currentContext = signupOwnsChallenge ? signupContext : loginContext;
    const pendingContext = RoutingTable.buildPendingMfaContext({
        currentContext,
        request: payload.request,
        response: payload.result
    });

    dispatch(signupOwnsChallenge
        ? context.actions.signup.updateContext(pendingContext)
        : context.actions.auth.updateLoginContext(pendingContext));

    return undefined;
};


internals.getCurrentAuthContext = ({ selectors, state }) => {

    const signupContext = selectors.getSignupContext?.(state);

    if (signupContext?.authMethod) {
        return signupContext;
    }

    return selectors.getLoginContext?.(state) || {};
};


internals.getResumableMfaContext = ({ selectors, state }) => {

    const signupContext = selectors.getSignupContext?.(state) || {};

    if (signupContext.mfaToken) {
        return signupContext;
    }

    return selectors.getLoginContext?.(state) || {};
};


internals.consumeLoginContinuation = (context) => {

    const redux = context.redux.hooks;
    const selectors = context.selectors.all;
    const loginContext = selectors.getLoginContext?.(redux.getState()) || {};

    return AuthStateTransitions.completeSuccessfulAuthentication(
        loginContext.lastLoginEmail || loginContext.email
    );
};


internals.clearTransientAuthState = ({ preserveContinuation = false } = {}) => {

    AuthPersistence.clearTransientAuthState();

    if (!preserveContinuation) {
        Continuation.clear();
    }
};


internals.bootstrapMainApp = async (context, {
    redirect,
    schoolId,
    target
} = {}) => {

    return internals.initializeApp(context, schoolId, { redirect, target });
};


internals.initializeIncompleteSignupFromAuth = async (context, { signupContext = {} } = {}) => {

    const actions = context.actions;
    const redux = context.redux.hooks;
    const authFlow = RoutingTable.resolveAuthFlow({
        email: signupContext.email,
        response: signupContext,
        source: AuthConstants.authFlow.sources.incompleteSignupLogin
    });

    const normalizedSignupContext = {
        ...signupContext,
        ...RoutingTable.buildAuthFlowContextUpdate({ authFlow })
    };

    const {
        verificationToken,
        schoolId,
        roleId,
        email,
        sso,
        mfaMethod,
        authMethod,
        authStatus,
        authFlowSource,
        hasPassword,
        hasSSOToken,
        isEmailVerified
    } = normalizedSignupContext;

    if (!verificationToken || !schoolId || !email) {
        throw new Error('Missing incomplete-signup auth context');
    }

    redux.dispatch(actions.signup.initContext({
        verificationToken,
        schoolId,
        roleId,
        email,
        fetchedProgress: false,
        isVerified: false,
        isEmailVerified,
        sso,
        mfaMethod,
        authMethod,
        authStatus,
        authFlowSource,
        hasPassword,
        hasSSOToken,
        agreeToTerms: AuthPersistence.getBrowserTermsAgreement()
    }));

    await initializeAppForSignup(context);

    if (context.selectors.all.getSignupContext(redux.getState()).verificationToken !== verificationToken) {
        return;
    }

    return redux.dispatch(Push(internals.getIncompleteSignupDestination({
        signupContext: normalizedSignupContext
    })));
};


internals.getIncompleteSignupDestination = ({ signupContext }) => {

    const path = RoutingTable.getIncompleteSignupRoute({ signupContext });
    const query = new URLSearchParams({
        v: signupContext.verificationToken
    });

    return `${path}?${query.toString()}`;
};


internals.initializeApp = async (context, schoolId, { redirect, target } = {}) => {

    const { redux: { hooks: redux }, selectors: { all: selectors }, actions } = context;
    const token = selectors.getApiToken?.(redux.getState());
    const existing = internals.initializations.get(context);

    if (existing && existing.token === token) {
        if (target) {
            existing.request = { schoolId, redirect, target };
        }

        return existing.promise;
    }

    const record = { token, request: { schoolId, redirect, target } };
    let resolve;
    let reject;
    record.promise = new Promise((yes, no) => { resolve = yes; reject = no; });
    internals.initializations.set(context, record);

    const isCurrent = () => selectors.getIsAuthenticated(redux.getState())
        && selectors.getApiToken?.(redux.getState()) === token;
    const run = async () => {

        while (isCurrent()) {
            const request = record.request;
            await internals.performInitializeApp(context, request.schoolId, request);

            if (!isCurrent()) {
                return;
            }

            const latest = record.request;
            const requestedSchoolId = latest.target
                ? RoutingTable.getRequestedSchoolId(new URL(latest.target, 'https://nearpeer.net')) : undefined;
            const currentSchoolId = selectors.getCurrentSchool?.(redux.getState())?.id
                || selectors.getCurrentSchoolId?.(redux.getState());

            if (latest !== request && requestedSchoolId && requestedSchoolId !== Number(currentSchoolId)
                && Continuation.peek().target === latest.target) {
                continue;
            }

            redux.dispatch(actions.app.setMainAppLoading({ appLoading: false }));

            if (latest.target && Continuation.peek().target === latest.target
                && window.location.pathname.startsWith('/app/')
                && window.location.pathname !== AuthConstants.authFlow.routes.app.loader
                && !Continuation.isDeferred()
                && (!requestedSchoolId || requestedSchoolId === Number(currentSchoolId))) {
                Continuation.consume();
                redux.dispatch(Replace(latest.target));
            }

            return;
        }
    };

    run().finally(() => {

        if (internals.initializations.get(context) === record) {
            internals.initializations.delete(context);
        }
    }).then(resolve, reject);

    return record.promise;
};


internals.performInitializeApp = async (context, schoolId, { redirect, target } = {}) => {

    // Needs to be lazily pulled from context due to app initialize order.
    const communication = CreateCommunicationService.fromContext(context);
    const actions = context.actions;
    const redux   = context.redux.hooks;
    const selectors = context.selectors.all;
    const requestedSchoolId = RoutingTable.getRequestedSchoolId(target ? new URL(target, 'https://nearpeer.net') : undefined);
    const initialState = redux.getState();
    const activeToken = selectors.getApiToken?.(initialState);
    const isCurrentSession = () => selectors.getIsAuthenticated(redux.getState())
        && selectors.getApiToken?.(redux.getState()) === activeToken;
    const cachedSchoolId = selectors.getCurrentSchool?.(initialState)?.id || selectors.getCurrentSchoolId?.(initialState);
    const hasCachedData = selectors.hasCurrentUser?.(initialState)
        && selectors.hasCurrentSchool?.(initialState)
        && (!requestedSchoolId || requestedSchoolId === Number(cachedSchoolId));

    redux.dispatch(actions.app.setMainAppLoading({ appLoading: !hasCachedData }));

    if (!NoRedirectRoutes.includes(window.location.pathname)
        && !(hasCachedData && window.location.pathname.startsWith('/app/'))) {
        const isCordovaApp = !!window.cordova;
        const currentPath = `${window.location.pathname || ''}${window.location.search || ''}`;
        const currentRedirect = RoutingTable.getRedirectContext();
        const isAppDestination = window.location.pathname.startsWith('/app/')
            && window.location.pathname !== AuthConstants.authFlow.routes.app.loader;
        const loaderRoute = RoutingTable.getAppLoaderRoute({
            redirect: redirect || currentRedirect.redirect || (isAppDestination
                ? RoutingTable.REDIRECTS.afterAuth
                : RoutingTable.REDIRECTS.welcome),
            target: target || currentRedirect.target || (isAppDestination
                ? currentPath
                : undefined)
        });

        if (currentPath !== loaderRoute) {
            console.info('[auth] initializeApp replacing route with loader', {
                currentPath,
                loaderRoute,
                schoolId,
                isCordovaApp
            });
            redux.dispatch(Replace(loaderRoute));
        }
        else {
            console.info('[auth] initializeApp already on loader route', {
                currentPath,
                schoolId
            });
        }
    }

    try {
        await communication.initialize();

        if (!isCurrentSession()) {
            return;
        }

        const userResponse = await measureStartupTask('login', 'user', () => redux.dispatch(
            actions.dataFetching.fetchCurrentUser({ throwOnError: true })
        ));

        if (!isCurrentSession()) {
            return;
        }

        const state = redux.getState();
        const currentSchoolId = selectors.getCurrentSchool?.(state)?.id
            || selectors.getCurrentSchoolId?.(state)
            || schoolId;

        if (requestedSchoolId && currentSchoolId && requestedSchoolId !== Number(currentSchoolId)) {

            // Eligibility depends on both the current user and community
            // metadata. Await their Redux updates before deciding to switch.
            await redux.dispatch(actions.dataFetching.fetchAppEntities({ entities: ['schools'], schoolId: Number(currentSchoolId) }));
            if (!isCurrentSession()) {
                return;
            }

            const communities = selectors.getSwitchableCommunities(redux.getState());

            if (communities.some(({ id }) => Number(id) === requestedSchoolId)) {
                const continuation = Continuation.peek();

                await redux.dispatch(actions.profileManagement.switchSchool({
                    schoolId: requestedSchoolId,
                    redirect: continuation.redirect,
                    target: continuation.target
                }));
                return;
            }

            const invites = selectors.getJoinableCommunities?.(redux.getState()) || [];
            const isInvited = invites.some(({ id }) => Number(id) === requestedSchoolId);
            if (isInvited) {
                Continuation.deferForCommunity({ schoolId: requestedSchoolId, target: target || Continuation.peek().target });
            }
            else {
                Continuation.save({ redirect: RoutingTable.REDIRECTS.welcome });
            }

            redux.dispatch(actions.alerts.notification.push({
                displayType: 'snackbar',
                message: isInvited
                    ? 'Join the invited community to open this link. We’ll return you to it after you confirm your profile.'
                    : 'The community linked in this notification is not available to your account.'
            }));
        }

        const resolvedSchoolId = currentSchoolId
            || schoolId
            || selectors.getLoginContext?.(state)?.schoolId;

        if (!resolvedSchoolId) {
            throw new Error('Cannot bootstrap main app without a school id');
        }

        const bootstrapResponse = await measureStartupTask('login', 'entities', () => redux.dispatch(
            actions.dataFetching.fetchAppEntities({
                entities: internals.MAIN_APP_BOOTSTRAP_ENTITIES,
                schoolId: resolvedSchoolId
            })
        ));

        if (!isCurrentSession()) {
            return;
        }

        await measureStartupTask('login', 'cache', () => BootstrapCache.save({
            token: activeToken,
            schoolId: resolvedSchoolId,
            user: userResponse?.payload?.result,
            bootstrap: bootstrapResponse?.payload?.result,
            isCurrent: isCurrentSession
        }));

        if (isCurrentSession()) {
            scheduleBackgroundStartup({ context, isCurrent: isCurrentSession, source: 'login' });
        }
    }
    catch (err) {
        if (!isCurrentSession()) {
            return;
        }

        return recoverFromBootstrapError({
            actions,
            authActions: actions.auth,
            error: err,
            logContext: '[auth] initializeApp failed after login',
            redux,
            selectors
        });
    }

};

internals.MAIN_APP_BOOTSTRAP_ENTITIES = [
    'schools',
    'schoolDetails',
    'badgeTypes',
    'conversationStarters',
    'yearsHired',
    'offices',
    'roles',
    'classes',
    'preferences',
    'roleGroups'
];
